import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Bin, type Condition, type Location, type MainCategory } from './api';

export function useConditions() {
  return useQuery({ queryKey: ['conditions'], queryFn: () => api<{ conditions: Condition[] }>('/conditions').then((r) => r.conditions) });
}

export function useLocations(all = false) {
  return useQuery({
    queryKey: ['locations', all],
    queryFn: () => api<{ locations: Location[] }>(`/locations${all ? '?all=1' : ''}`).then((r) => r.locations),
  });
}

/** Active bins, for "choose a location or bin" lists. */
export function useBins() {
  return useQuery({ queryKey: ['bins', 'active'], queryFn: () => api<{ bins: Bin[] }>('/bins').then((r) => r.bins) });
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () =>
      api<{ settings: { defaultCurrency: string; allowUnissuedLabels: boolean; costPercent: Record<MainCategory, number> } }>('/settings').then((r) => r.settings),
  });
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** State mirrored into localStorage so typed input survives a reload or dropped connection. */
export function useStoredState<T>(key: string, initial: () => T): [T, (v: T | ((p: T) => T)) => void, boolean] {
  const [restored] = useState(() => {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  });
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return { ...initial(), ...JSON.parse(raw) };
    } catch {
      /* storage unavailable */
    }
    return initial();
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* storage unavailable or full */
    }
  }, [key, state]);
  return [state, setState, restored];
}

export function clearStored(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
